import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  message,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Divider,
  Layout,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  LockOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../store/authStore';
import { roleAPI } from '../../services/api';
import AppHeader from '../../components/Header';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Content } = Layout;

interface Role {
  id: string;
  role_name: string;
  name: string;
  description: string;
  permissions: string[];
  color: string;
  is_system: boolean;
  created_at?: string;
  updated_at?: string;
}

interface Permission {
  key: string;
  label: string;
}

const RoleManagementPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [form] = Form.useForm();
  const { user } = useAuthStore();
  const loadingRef = useRef({ roles: false, permissions: false });

  // Color options for roles
  const colorOptions = [
    { label: 'Blue', value: '#1890ff' },
    { label: 'Green', value: '#52c41a' },
    { label: 'Orange', value: '#fa8c16' },
    { label: 'Red', value: '#ff4d4f' },
    { label: 'Purple', value: '#722ed1' },
    { label: 'Cyan', value: '#13c2c2' },
    { label: 'Magenta', value: '#eb2f96' },
  ];

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    if (loadingRef.current.roles) return;
    
    try {
      loadingRef.current.roles = true;
      setLoading(true);
      const response = await roleAPI.getRoles();
      if (response.data.success) {
        setRoles(response.data.roles || []);
      }
    } catch (error: any) {
      message.error('Failed to fetch roles');
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
      loadingRef.current.roles = false;
    }
  };

  const fetchPermissions = async () => {
    if (loadingRef.current.permissions) return;
    
    try {
      loadingRef.current.permissions = true;
      const response = await roleAPI.getAvailablePermissions();
      if (response.data.success) {
        setPermissions(response.data.permissions || []);
      }
    } catch (error: any) {
      message.error('Failed to fetch permissions');
      console.error('Error fetching permissions:', error);
    } finally {
      loadingRef.current.permissions = false;
    }
  };

  const handleCreate = () => {
    setEditingRole(null);
    form.resetFields();
    form.setFieldsValue({ color: '#1890ff' });
    setModalVisible(true);
  };

  const handleEdit = (role: Role) => {
    if (role.is_system) {
      message.warning('System roles cannot be edited');
      return;
    }
    setEditingRole(role);
    form.setFieldsValue({
      role_name: role.role_name,
      description: role.description,
      permissions: role.permissions,
      color: role.color,
    });
    setModalVisible(true);
  };

  const handleDelete = async (role: Role) => {
    if (role.is_system) {
      message.warning('System roles cannot be deleted');
      return;
    }

    try {
      await roleAPI.deleteRole(role.id);
      message.success('Role deleted successfully');
      fetchRoles();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete role';
      message.error(errorMessage);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Debug: Log what permissions we're sending
      console.log('Creating/updating role with permissions:', values.permissions);
      console.log('Available permission options:', permissions);
      
      if (editingRole) {
        // Update existing role
        await roleAPI.updateRole(editingRole.id, {
          description: values.description,
          permissions: values.permissions,
          color: values.color,
        });
        message.success('Role updated successfully');
      } else {
        // Create new role with detailed permissions (e.g., 'analytics:read', 'user_management:write')
        await roleAPI.createRole({
          role_name: values.role_name,
          description: values.description,
          permissions: values.permissions, // These are the detailed permissions like 'analytics:read'
          color: values.color,
        });
        message.success('Role created successfully');
      }

      setModalVisible(false);
      fetchRoles();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to save role';
      message.error(errorMessage);
    }
  };

  const columns = [
    {
      title: 'Role Name',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (text: string, record: Role) => (
        <Space>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: record.color,
            }}
          />
          <Text strong>{record.role_name}</Text>
          {record.is_system && (
            <Tag color="blue">
              System
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {text}
        </Text>
      ),
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions: string[]) => (
        <div>
          {permissions.slice(0, 3).map((permission) => {
            const permissionLabel = permissionLabels[permission] || permission;
            return (
              <Tag key={permission} style={{ margin: '2px' }}>
                {permissionLabel}
              </Tag>
            );
          })}
          {permissions.length > 3 && (
            <Tooltip
              title={
                <div>
                  {permissions.slice(3).map((permission) => (
                    <div key={permission}>
                      {permissionLabels[permission] || permission}
                    </div>
                  ))}
                </div>
              }
            >
              <Tag style={{ margin: '2px' }}>+{permissions.length - 3} more</Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: Role) => (
        <Space>
          <Tooltip title={record.is_system ? 'System roles cannot be edited' : 'Edit role'}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={record.is_system}
            />
          </Tooltip>
          <Tooltip title={record.is_system ? 'System roles cannot be deleted' : 'Delete role'}>
            <Popconfirm
              title="Are you sure you want to delete this role?"
              description="This action cannot be undone. Users with this role will need to be reassigned."
              onConfirm={() => handleDelete(record)}
              okText="Yes, Delete"
              cancelText="Cancel"
              disabled={record.is_system}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={record.is_system}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Create a mapping for permission labels
  const permissionLabels: Record<string, string> = {};
  permissions.forEach((perm) => {
    permissionLabels[perm.key] = perm.label;
  });

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeader />
      <Content style={{ padding: '24px' }}>
        <div className="role-management-page">
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <div>
                <Title level={3} style={{ margin: 0, color: '#47569e' }}>
                  <SettingOutlined style={{ marginRight: '8px' }} />
                  Role Management
                </Title>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  Manage user roles and permissions for your organization
                </Text>
              </div>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Create Role
              </Button>
            </div>

            <Table
              columns={columns}
              dataSource={roles}
              loading={loading}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} roles`,
              }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={editingRole ? 'Edit Role' : 'Create New Role'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ color: '#1890ff' }}
        >
          {!editingRole && (
            <Form.Item
              name="role_name"
              label="Role Name"
              rules={[
                { required: true, message: 'Please enter role name' },
                { pattern: /^[a-z_]+$/, message: 'Role name must be lowercase with underscores only' },
              ]}
            >
              <Input
                placeholder="e.g., content_manager, data_analyst"
                prefix={<UserOutlined />}
              />
            </Form.Item>
          )}

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea
              rows={3}
              placeholder="Describe what this role can do..."
            />
          </Form.Item>

          <Form.Item
            name="permissions"
            label="Permissions"
            rules={[{ required: true, message: 'Please select at least one permission' }]}
            extra={
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Select specific permissions for this role. These use the format 'feature:action' (e.g., 'analytics:read', 'user_management:write')
              </Text>
            }
          >
            <Select
              mode="multiple"
              placeholder="Select permissions"
              optionFilterProp="children"
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) =>
                (option?.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
              }
            >
              {permissions.map((permission) => (
                <Select.Option key={permission.key} value={permission.key}>
                  <LockOutlined style={{ marginRight: '8px' }} />
                  {permission.label}
                  <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                    ({permission.key})
                  </Text>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="color"
            label="Color"
            rules={[{ required: true, message: 'Please select a color' }]}
          >
            <Select placeholder="Select role color">
              {colorOptions.map((color) => (
                <Select.Option key={color.value} value={color.value}>
                  <Space>
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: color.value,
                        display: 'inline-block',
                      }}
                    />
                    {color.label}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
        </div>
      </Content>
    </Layout>
  );
};

export default RoleManagementPage;
